/**
 * Семантические цвета модуля — единственный источник цвета для компонентов.
 *
 * Заменяет россыпь литералов (~300 вхождений в 16 файлах): интерфейс был покрашен
 * дефолтной палитрой antd, скопированной hex'ами. Пока тема была одна, это не
 * проявлялось — значения совпадали с токенами. В тёмной теме совпадение исчезло:
 * `darkAlgorithm` перекрасил компоненты antd, а зашитые hex'ы остались светлыми,
 * и половина интерфейса стала нечитаемой (скриншот с платформы 2026-08-21).
 *
 * Роли названы по смыслу («фон успеха»), а не по виду («светло-зелёный»): в тёмной
 * теме фон успеха тёмный, и имя, описывающее вид, соврало бы ровно там, где ошибку
 * труднее всего заметить.
 *
 * Соответствие бывших литералов ролям — таблицей, чтобы правка любого из этих мест
 * не начиналась с археологии:
 *
 * | Было                                            | Роль            |
 * |-------------------------------------------------|-----------------|
 * | `#f6ffed` `#ecfdf5`                             | `successBg`     |
 * | `#b7eb8f` `#a7f3d0`                             | `successBorder` |
 * | `#52c41a` `#3f8600` `#047857`                   | `success`       |
 * | `#fff2f0` `#fef2f2`                             | `errorBg`       |
 * | `#ffccc7` `#ffa39e` `#fecaca`                   | `errorBorder`   |
 * | `#ff4d4f` `#ff7875` `#cf1322` `#a8071a` `#dc2626` | `error`       |
 * | `#e6f7ff` `#e6f4ff` `#f0f5ff` `#eff6ff`         | `infoBg`        |
 * | `#91d5ff` `#91caff` `#adc6ff` `#bfdbfe`         | `infoBorder`    |
 * | `#1890ff` `#1677ff` `#69b1ff` `#2563eb`         | `info`          |
 * | `#fffbe6` `#fff7e6` `#fffbeb`                   | `warningBg`     |
 * | `#ffe58f` `#ffd591` `#fde68a`                   | `warningBorder` |
 * | `#faad14` `#fa8c16` `#d46b08` `#b45309` `#d99f32` `#d49a27` | `warning` |
 * | `#f9f0ff` `#f5f3ff`                             | `accentBg`      |
 * | `#d3adf7` `#ddd6fe` `#c4b5fd`                   | `accentBorder`  |
 * | `#722ed1` `#7c3aed` `#8b5cf6` `#6d28d9`         | `accent`        |
 * | `#fafafa` `#f8fafc`                             | `fillSubtle`    |
 * | `#f5f5f5` `#f1f5f9`                             | `fillMuted`     |
 * | `#f0f0f0` `#e8e8e8` `#e2e8f0` `#dbe2ea`         | `borderSubtle`  |
 * | `#999` `#8c8c8c` `#a8b1c2` `#64748b`            | `textTertiary`  |
 * | `#666` `#595959`                                | `textSecondary` |
 * | `#1f2937` `#111827`                             | `text`          |
 * | `#001029` как ФОН шапки                          | `brandBar`      |
 * | `#fff` на залитом акцентом фоне                 | `onFilled`      |
 * | `#fff` `#171b27` как поверхность карточки       | `surface`       |
 */

import { colorPrimitives } from '@aseller/antd-theme';
import { theme } from 'antd';
import { useMemo } from 'react';
import type { GlobalToken } from 'antd';

/**
 * Фон брендовой полосы — намеренно НЕ адаптивный.
 *
 * `#001029` в старом коде стоял и как цвет текста, и как фон шапки. Как текст он
 * обязан светлеть в тёмной теме, как фон — обязан оставаться тёмным, иначе белые
 * надписи на нём исчезнут. Одна роль этих двух требований не выдерживает, поэтому
 * фон получил свою — со значением из примитивов дизайн-системы, у которых режима нет.
 *
 * Сегодня полоса живёт только в standalone (в embed хром даёт платформа), то есть
 * тёмный режим до неё не доходит. Роль заведена не «на будущее», а потому что
 * `background: colorText` — это готовая ловушка для того, кто включит тёмный режим
 * в standalone: сломается не там, где будут искать.
 */
const BRAND_BAR_BG = colorPrimitives['neutrals-color-black'];

export interface AppColors {
  /**
   * Сигнальные цвета — для заливок, рамок и иконок. Для ТЕКСТА — роли `*Text`: те же
   * оттенки, доведённые до контраста 4,5 на карточке и на своей плашке (appTheme.ts).
   */
  successText: string;
  warningText: string;
  errorText: string;
  infoText: string;

  /** Успех: положительная прибыль, маржа, ROI. */
  success: string;
  successBg: string;
  successBorder: string;

  /** Предупреждение: промо, купоны, пороговые значения. */
  warning: string;
  warningBg: string;
  warningBorder: string;

  /** Ошибка и отрицательные значения. */
  error: string;
  errorBg: string;
  errorBorder: string;

  /** Информация и подсветка расчётных (не введённых) величин. */
  info: string;
  infoBg: string;
  infoBorder: string;

  /** Акцент второго плана: доля в процентах, пунктир прогноза, changelog. */
  accent: string;
  accentBg: string;
  accentBorder: string;

  /** Текст основной, приглушённый и подписи полей. */
  text: string;
  textSecondary: string;
  textTertiary: string;

  /** Текст поверх залитого цветом фона — белый в обеих темах. */
  onFilled: string;

  /** Фон брендовой полосы: тёмный в обеих темах, текст поверх него — `onFilled`. */
  brandBar: string;

  /** Поверхность карточки и подложки под неактивными/расчётными полями. */
  surface: string;
  fillSubtle: string;
  fillMuted: string;

  /** Границы: обычная и еле заметная (разделители, рамки подложек). */
  border: string;
  borderSubtle: string;
}

/**
 * Цвета из готового токена темы.
 *
 * Отдельно от хука, потому что часть цвета нужна вне рендера — конфигурация графика
 * и таблицы собирается функциями, которым хук вызвать негде.
 */
export function appColors(token: GlobalToken): AppColors {
  return {
    successText: token.colorSuccessText,
    warningText: token.colorWarningText,
    errorText: token.colorErrorText,
    infoText: token.colorInfoText,

    success: token.colorSuccess,
    successBg: token.colorSuccessBg,
    successBorder: token.colorSuccessBorder,

    warning: token.colorWarning,
    warningBg: token.colorWarningBg,
    warningBorder: token.colorWarningBorder,

    error: token.colorError,
    errorBg: token.colorErrorBg,
    errorBorder: token.colorErrorBorder,

    info: token.colorInfo,
    infoBg: token.colorInfoBg,
    infoBorder: token.colorInfoBorder,

    // Фиолетовый акцент берётся из палитры темы, а не задаётся своим значением:
    // preset-палитры antd тоже проходят через алгоритм, поэтому в тёмной теме
    // purple6 темнеет вместе со всем остальным.
    accent: token.purple6,
    accentBg: token.purple1,
    accentBorder: token.purple3,

    text: token.colorText,
    textSecondary: token.colorTextSecondary,
    textTertiary: token.colorTextTertiary,

    onFilled: token.colorWhite,

    brandBar: BRAND_BAR_BG,

    surface: token.colorBgContainer,
    fillSubtle: token.colorFillQuaternary,
    fillMuted: token.colorFillTertiary,

    border: token.colorBorder,
    borderSubtle: token.colorBorderSecondary,
  };
}

/**
 * Цвета активной темы. Следует `ConfigProvider`, поэтому переключение бесплатно.
 *
 * Результат мемоизирован по токену, и это не микрооптимизация: возвращаемый объект
 * попадает в списки зависимостей `useMemo`/`useCallback` у вызывающих (например, в
 * фабрике колонок таблицы конкурентов). Новый объект на каждый рендер молча
 * обессмыслил бы там всю мемоизацию — код остался бы рабочим, но пересчитывал
 * колонки на каждое нажатие клавиши, и связь с этим файлом никто бы не нашёл.
 */
export function useAppColors(): AppColors {
  const { token } = theme.useToken();
  return useMemo(() => appColors(token), [token]);
}
